import { styled } from 'goober';
import type { GristRecord } from '../../../types';
import { isHttpUrl } from '../../../utils';

const Container = styled('div')`
  min-width: 240px;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  background: var(--bg);
`;

const Header = styled('div')`
  padding: 10px 12px;
  padding-right: 32px; /* Space for close button */
  background: #f8f9fa;
  border-bottom: 1px solid var(--muted-border);
  font-weight: 600;
  font-size: 13px;
  color: var(--text);
  display: flex;
  align-items: center;
  min-height: 20px;
`;

const Body = styled('div')`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TypeBadge = styled('span')`
  display: inline-block;
  padding: 2px 6px;
  background: var(--light-bg);
  border: 1px solid var(--muted-border);
  border-radius: 4px;
  font-size: 11px;
  color: var(--text);
  font-weight: 500;
  align-self: flex-start;
`;

const InfoSection = styled('div')`
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--muted-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoItem = styled('div')`
  font-size: 12px;
  color: var(--text);
  line-height: 1.4;
  word-break: break-word;

  strong {
    font-weight: 600;
    color: var(--text-muted);
    margin-right: 4px;
    font-size: 11px;
    text-transform: uppercase;
  }

  a {
    color: var(--accent);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

interface MarkerPopupProps {
  name: string;
  type?: string;
  record: GristRecord;
  infoColumns: string[];
}

export function MarkerPopup({ name, type, record, infoColumns }: MarkerPopupProps) {
  const infoItems = infoColumns
    .map((colName) => {
      const value = record[colName];
      if (value !== null && value !== undefined && value !== '') {
        return { colName, value: String(value) };
      }
      return null;
    })
    .filter(Boolean) as { colName: string; value: string }[];

  return (
    <Container>
      <Header>{name}</Header>
      <Body>
        {type && <TypeBadge>{type}</TypeBadge>}

        {infoItems.length > 0 && (
          <InfoSection>
            {infoItems.map(({ colName, value }, index) => (
              <InfoItem key={index}>
                <strong>{colName}</strong>
                {isHttpUrl(value) ? (
                  <a href={value} target="_blank" rel="noopener noreferrer">
                    {value}
                  </a>
                ) : (
                  value
                )}
              </InfoItem>
            ))}
          </InfoSection>
        )}
      </Body>
    </Container>
  );
}
